import { WebActionEvents } from '@griply/types';
import {
  SetupChecklistState,
  useSetupChecklistDisplayItems,
} from 'features/setup-checklist';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SetupChecklistCard } from 'shared/components/ui/setup-checklist-card';
import { useTrackEvents } from 'shared/hooks/use-track-events';

export type SetupChecklistContentProps = {
  state: SetupChecklistState;
};

export const SetupChecklistContent: React.FC<SetupChecklistContentProps> = ({
  state,
}) => {
  const { t } = useTranslation();
  const track = useTrackEvents();
  const { displayItems, completeCount, totalCount, allComplete } =
    useSetupChecklistDisplayItems();

  const hasTrackedShown = useRef(false);
  const hasTrackedCompleted = useRef(false);

  const onHide = useCallback(() => {
    track(WebActionEvents.SetupChecklistDismissed, undefined);
    state.dismiss();
  }, [track, state]);
  const onToggleCollapse = useCallback(
    () => state.setCollapsed(!state.collapsed),
    [state],
  );

  useEffect(() => {
    if (hasTrackedShown.current) {
      return;
    }
    hasTrackedShown.current = true;
    track(WebActionEvents.SetupChecklistShown, undefined);
  }, [track]);

  useEffect(() => {
    if (allComplete && !state.everComplete) {
      if (!hasTrackedCompleted.current) {
        hasTrackedCompleted.current = true;
        track(WebActionEvents.SetupChecklistCompleted, undefined);
      }
      state.markComplete();
    }
  }, [allComplete, state, track]);

  return (
    <SetupChecklistCard
      title={t('setup-checklist.title')}
      items={displayItems}
      completeCount={completeCount}
      totalCount={totalCount}
      collapsed={state.collapsed}
      onToggleCollapse={onToggleCollapse}
      onHide={onHide}
      hideLabel={t('setup-checklist.remove')}
    />
  );
};
